import { formatCents } from "@/lib/money";

import { ReportSection, Stat, StatGrid } from "../ReportPrimitives";
import type { ForecastReport } from "../reportTables";

/**
 * The weighted pipeline on a calendar: per month, what is already booked
 * (invoices), what is LIKELY (open deals × probability on their expected
 * close month), and the full-value ceiling.
 *
 * Bars are drawn as stacked booked+weighted against the best case, because
 * the useful read is "how much of the ceiling is actually likely", a lone
 * weighted number hides whether it is 90% or 9% of what is on the table.
 */
export const ForecastPanel = ({ data }: { data: ForecastReport }) => {
  const next90 = data.months
    .slice(0, 3)
    .reduce((sum, month) => sum + month.weighted_cents, 0);
  const bookedThisMonth = data.months[0]?.booked_cents ?? 0;
  const max = Math.max(
    ...data.months.map((month) =>
      Math.max(
        month.best_case_cents,
        month.booked_cents + month.weighted_cents,
      ),
    ),
    1,
  );

  return (
    <div className="flex flex-col gap-4">
      <StatGrid>
        <Stat
          label="Likely next 90 days"
          value={formatCents(next90)}
          helper="Open deals × probability"
        />
        <Stat
          label="Booked this month"
          value={formatCents(bookedThisMonth)}
          helper="Invoices already issued"
          tone="positive"
        />
        <Stat
          label="Undated pipeline"
          value={formatCents(data.undated_weighted_cents)}
          helper={`${data.undated_deals} open deal${
            data.undated_deals === 1 ? "" : "s"
          } with no (future) close date, worth dating`}
          tone={data.undated_deals > 0 ? "negative" : undefined}
        />
      </StatGrid>

      <ReportSection
        title="Monthly outlook"
        subtitle="Solid: booked · shaded: likely (weighted) · outline: best case"
      >
        <div className="flex flex-col gap-2.5">
          {data.months.map((month) => (
            <div key={month.month_start} className="min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] font-medium text-foreground">
                  {month.label}
                </span>
                <span className="text-[12.5px] tabular-nums text-muted-foreground">
                  {formatCents(month.booked_cents + month.weighted_cents)}
                  <span className="text-[11px]">
                    {" "}
                    / {formatCents(month.best_case_cents)} best case
                  </span>
                </span>
              </div>
              <div className="relative mt-1 h-3 overflow-hidden rounded-full border border-border bg-muted/40">
                <div
                  className="absolute inset-y-0 left-0 rounded-full border border-primary/30"
                  style={{
                    width: `${(month.best_case_cents / max) * 100}%`,
                  }}
                />
                <div
                  className="absolute inset-y-0 left-0 bg-primary/35"
                  style={{
                    width: `${((month.booked_cents + month.weighted_cents) / max) * 100}%`,
                  }}
                />
                <div
                  className="absolute inset-y-0 left-0 bg-primary"
                  style={{ width: `${(month.booked_cents / max) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </ReportSection>
    </div>
  );
};
